import { useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { apiFetch } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import type { User } from "@/types/auth";

/**
 * Lands here after Google OAuth. The backend redirects to
 * `/auth/callback#token=<jwt>`. We pull the token out of the URL fragment,
 * stash it, fetch /auth/me, and navigate home.
 */
export function AuthCallbackPage() {
  const router = useRouter();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fragment = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(fragment);
    const token = params.get("token");

    if (!token) {
      setError("Missing token in callback URL");
      return;
    }

    // Persist the token immediately so apiFetch picks it up.
    useAuthStore.setState({ token, isAuthenticated: true });

    apiFetch<User>("/auth/me")
      .then((user) => {
        setAuth(token, user);
        // Strip the fragment and navigate home.
        window.history.replaceState(null, "", "/");
        void router.navigate({ to: "/" });
      })
      .catch((err: Error) => {
        useAuthStore.getState().logout();
        setError(err.message ?? "Failed to complete sign-in");
      });
  }, [router, setAuth]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md p-6 text-center">
        {error ? (
          <>
            <p className="text-sm text-danger">{error}</p>
            <button
              type="button"
              onClick={() => void router.navigate({ to: "/login" })}
              className="mt-4 rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-3 hover:text-ink"
            >
              Back to Sign In
            </button>
          </>
        ) : (
          <p className="text-sm text-ink-2">
            Completing sign-in...
          </p>
        )}
      </div>
    </div>
  );
}
